/**
 * Request Deduplication Service
 * Prevents duplicate API calls by tracking ongoing requests and caching responses
 */

class RequestDeduplicationService {
  constructor() {
    this.pendingRequests = new Map();
    this.responseCache = new Map();
    this.cacheExpiry = new Map();
    this.defaultCacheTime = 30000; // 30 seconds default cache time
  }

  /**
   * Generate a cache key for a request
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Object} params - Query parameters or body
   * @returns {string} Cache key
   */
  generateCacheKey(method, url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${JSON.stringify(params[key])}`)
      .join('&');
    
    return `${method}:${url}${sortedParams ? `?${sortedParams}` : ''}`;
  }

  /**
   * Check if a request is already in progress
   * @param {string} cacheKey - The cache key for the request
   * @returns {boolean} True if request is pending
   */
  isRequestPending(cacheKey) {
    return this.pendingRequests.has(cacheKey);
  }

  /**
   * Check if we have a valid cached response
   * @param {string} cacheKey - The cache key for the request
   * @returns {Object|null} Cached response or null if expired/missing
   */
  getCachedResponse(cacheKey) {
    const cached = this.responseCache.get(cacheKey);
    const expiry = this.cacheExpiry.get(cacheKey);
    
    if (cached && expiry && Date.now() < expiry) {
      console.log(`RequestDeduplication: Using cached response for ${cacheKey}`);
      return cached;
    }
    
    // Clean up expired cache
    if (expiry && Date.now() >= expiry) {
      this.responseCache.delete(cacheKey);
      this.cacheExpiry.delete(cacheKey);
    }
    
    return null;
  }

  /**
   * Store a response in cache
   * @param {string} cacheKey - The cache key for the request
   * @param {Object} response - The response to cache
   * @param {number} cacheTime - Cache duration in milliseconds
   */
  cacheResponse(cacheKey, response, cacheTime = this.defaultCacheTime) {
    this.responseCache.set(cacheKey, response);
    this.cacheExpiry.set(cacheKey, Date.now() + cacheTime);
    console.log(`RequestDeduplication: Cached response for ${cacheKey}, expires in ${cacheTime}ms`);
  }

  /**
   * Track a pending request
   * @param {string} cacheKey - The cache key for the request
   * @returns {Promise} Promise that resolves when the request completes
   */
  trackRequest(cacheKey) {
    const existing = this.pendingRequests.get(cacheKey);
    if (existing) {
      console.log(`RequestDeduplication: Request already in progress for ${cacheKey}, waiting...`);
      return existing.promise;
    }

    // Create a new promise and store both the promise and its resolve/reject functions
    let resolveFunc, rejectFunc;
    const requestPromise = new Promise((resolve, reject) => {
      resolveFunc = resolve;
      rejectFunc = reject;
    });

    // Store the promise along with its resolve/reject functions
    this.pendingRequests.set(cacheKey, {
      promise: requestPromise,
      resolve: resolveFunc,
      reject: rejectFunc
    });

    return requestPromise;
  }

  /**
   * Complete a tracked request
   * @param {string} cacheKey - The cache key for the request
   * @param {Object} response - The response data
   * @param {Error} error - Any error that occurred
   */
  completeRequest(cacheKey, response = null, error = null) {
    const pending = this.pendingRequests.get(cacheKey);
    
    if (pending) {
      if (error) {
        pending.reject(error);
      } else {
        pending.resolve(response);
      }
      
      this.pendingRequests.delete(cacheKey);
      console.log(`RequestDeduplication: Completed request for ${cacheKey}`);
    }
  }

  /**
   * Clear all caches (useful for logout or when data becomes stale)
   */
  clearAllCaches() {
    this.responseCache.clear();
    this.cacheExpiry.clear();
    this.pendingRequests.clear();
    console.log('RequestDeduplication: All caches cleared');
  }

  /**
   * Clear cache for specific URL patterns
   * @param {string} urlPattern - URL pattern to match (supports wildcards)
   */
  clearCacheForPattern(urlPattern) {
    const keysToDelete = [];
    
    for (const key of this.responseCache.keys()) {
      if (key.includes(urlPattern)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.responseCache.delete(key);
      this.cacheExpiry.delete(key);
    });
    
    console.log(`RequestDeduplication: Cleared cache for pattern ${urlPattern}, removed ${keysToDelete.length} entries`);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      cachedResponses: this.responseCache.size,
      cacheExpiries: this.cacheExpiry.size
    };
  }
}

// Create singleton instance
const requestDeduplicationService = new RequestDeduplicationService();

export default requestDeduplicationService;
